import React, { useEffect, useRef, useState } from "react";
import { Platform, View, Text, Image, StyleSheet, Animated, Easing, Pressable } from "react-native";
import { useTheme } from "../../../context/ThemeContext";
import { FONT_MONO } from "./_shared";

// Logos from the /assets/images/partners set. Sized per-logo so they read
// uniformly in the marquee (target visual height ≈ 30–44px).
// `whiten: true` applies a web-only `brightness(0) invert(1)` filter so a
// dark-colored logo renders as solid white on the dark marquee background.
const PARTNERS = [
  { id: "fsy",       src: require("../../../assets/images/partners/logo_four-seasons-yachts.png"),   w: 106, h: 58 },
  { id: "carnival",  src: require("../../../assets/images/partners/logo_carnivaluk.png"),            w: 172, h: 58 },
  { id: "cunard",    src: require("../../../assets/images/partners/logo_cunard.png"),                w: 170, h: 54, pad: 0 },
  { id: "pocruises", src: require("../../../assets/images/partners/logo_p-o-cruises.png"),           w: 186, h: 56 },
  { id: "disney",    src: require("../../../assets/images/partners/logo_disney.png"),                w: 120, h: 44 },
  { id: "ncl",       src: require("../../../assets/images/partners/logo_ncl.png"),                   w: 110, h: 44 },
  { id: "crystal",   src: require("../../../assets/images/partners/logo_crystal.png"),               w: 130, h: 36 },
  { id: "amentum",   src: require("../../../assets/images/partners/logo_amentum.png"),               w: 194, h: 50 },
  { id: "hap",       src: require("../../../assets/images/partners/logo-holland-america-group-new.png"), w: 150, h: 58, pad: 12 },
  { id: "halhotels", src: require("../../../assets/images/partners/logo_hal-hotels.png"),            w: 280, h: 30 },
  { id: "plodges",   src: require("../../../assets/images/partners/logo_princess-lodges.png"),       w: 140, h: 34, whiten: true },
  { id: "ccg",       src: require("../../../assets/images/partners/logo_canadian-coast-guard.png"),  w: 130, h: 42 },
  { id: "inmarsat",  src: require("../../../assets/images/partners/logo_inmarsat.png"),              w: 130, h: 42 },
];

// `filter` is a web-only CSS property; RN-Web passes it through, native ignores.
const WHITEN = Platform.OS === "web" ? { filter: "brightness(0) invert(1)" } : null;

const FULL_LOOP_MS = 38000;

// Track is rendered with the partner list duplicated back-to-back. We measure
// its real pixel width and translate by half (one full set), then loop. That
// produces a seamless infinite scroll regardless of viewport / font width.
// Pause-on-hover: when `hovered` flips, the effect tears down the current
// Animated.timing and restarts from the last position when un-hovered, so
// speed stays constant across pauses.
export default function NtVoxcraftTrustMarquee() {
  const { theme } = useTheme();
  const x = useRef(new Animated.Value(0)).current;
  const positionRef = useRef(0);
  const [trackWidth, setTrackWidth] = useState(0);
  const [hovered, setHovered] = useState(false);

  // Mirror the Animated value into a plain ref so the runner can resume
  // from the last position when the user un-hovers. useNativeDriver must be
  // false for addListener to fire, which is fine on web (ignored) and OK on
  // native for a small horizontal transform.
  useEffect(() => {
    const id = x.addListener(({ value }) => { positionRef.current = value; });
    return () => x.removeListener(id);
  }, [x]);

  useEffect(() => {
    if (!trackWidth) return;
    if (hovered) return; // paused — the previous effect cleanup stopped the timing
    let cancelled = false;
    let current = null;

    const run = () => {
      if (cancelled) return;
      const target = -trackWidth / 2;
      const total = Math.abs(target);
      const remaining = total - Math.abs(positionRef.current);
      const duration = remaining > 0 ? FULL_LOOP_MS * (remaining / total) : FULL_LOOP_MS;
      current = Animated.timing(x, {
        toValue: target,
        duration,
        easing: Easing.linear,
        useNativeDriver: false,
      });
      current.start(({ finished }) => {
        if (cancelled || !finished) return;
        x.setValue(0);
        positionRef.current = 0;
        run();
      });
    };

    run();
    return () => {
      cancelled = true;
      if (current) current.stop();
    };
  }, [trackWidth, hovered, x]);

  return (
    <View style={[styles.strip, { backgroundColor: theme.bg2, borderColor: theme.borderSoft }]}>
      <Text style={[styles.label, { color: theme.textMute }]}>TRUSTED ACROSS INDUSTRIES</Text>
      <View style={styles.marquee}>
        <Pressable
          onHoverIn={() => setHovered(true)}
          onHoverOut={() => setHovered(false)}
          style={styles.pressable}
        >
          <Animated.View
            style={[styles.track, { transform: [{ translateX: x }] }]}
            onLayout={(e) => setTrackWidth(e.nativeEvent.layout.width)}
          >
            {[...PARTNERS, ...PARTNERS].map((p, i) => (
              <View
                key={`${p.id}-${i}`}
                style={[styles.item, p.pad != null && { paddingHorizontal: p.pad }]}
              >
                <Image
                  source={p.src}
                  style={[
                    { width: p.w, height: p.h, resizeMode: "contain" },
                    p.whiten && WHITEN,
                  ]}
                />
              </View>
            ))}
          </Animated.View>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  strip: { paddingVertical: 28, borderTopWidth: 1, borderBottomWidth: 1 },
  label: {
    textAlign: "center", fontFamily: FONT_MONO,
    fontSize: 11, fontWeight: "700", letterSpacing: 1.6,
    textTransform: "uppercase", marginBottom: 22,
  },
  marquee: { overflow: "hidden" },
  pressable: { alignSelf: "flex-start" },
  track: { flexDirection: "row", alignItems: "center", alignSelf: "flex-start" },
  // Same gap rhythm as before so marquee tempo feels identical.
  item: {
    paddingHorizontal: 28,
    alignItems: "center", justifyContent: "center",
    height: 56,
  },
});
